import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../common/Logo";

import {
  NavSection,
  NavBar,
  LogoArea,
  DesktopMenu,
  NavLink,
  NavCta,
  HamBtn,
  HamIcon,
  Overlay,
  Drawer,
  DrawerHeader,
  DrawerClose,
  DrawerLinks,
  DrawerLinkItem,
  DrawerLink,
  DrawerLinkArrow,
  DrawerDivider,
  DrawerCta,
} from "./Navigation.styles";

// ─── Data ─────────────────────────────────────────────────────────────────────
export const NAV_LINKS = [
  { label: "Home", path: "/", end: true },
  { label: "Find Your Yoga", path: "/yoga-goals", end: false },
  { label: "Find Your Diet", path: "/diet-planner", end: false },
  { label: "Yoga for Health", path: "/yoga-for-health", end: false },
  { label: "Yoga Practices", path: "/online-yoga", end: false },
  { label: "Gallery", path: "/gallery", end: false },
  { label: "Blog", path: "/blog", end: false },
  { label: "About", path: "/about", end: false },
  { label: "Programs", path: "/packages", end: false },
];

// ─── Arrow icon ───────────────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Escape key closes drawer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const go = useCallback(
    (path) => {
      navigate(path);
      setOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [navigate],
  );

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <NavSection
        id="navigation"
        aria-label="Main navigation"
        data-scrolled={scrolled}
      >
        <NavBar>
          {/* Logo */}
          <LogoArea
            onClick={() => go("/")}
            role="link"
            aria-label="Yoga Shastra — home"
          >
            <Logo />
          </LogoArea>

          {/* Desktop links — real flex child, shares space with logo + CTA */}
          <DesktopMenu>
            {NAV_LINKS.map(({ label, path }) => (
              <li key={path}>
                <NavLink
                  $active={isActive(path)}
                  onClick={() => go(path)}
                  aria-current={isActive(path) ? "page" : undefined}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </DesktopMenu>

          {/* Desktop CTA */}
          <NavCta type="button" onClick={() => go("/contactUs")}>
            Contact Us <ArrowIcon />
          </NavCta>

          {/* Hamburger — mobile only */}
          <HamBtn
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="nav-drawer"
            onClick={() => setOpen((v) => !v)}
          >
            <HamIcon $open={open} />
          </HamBtn>
        </NavBar>
      </NavSection>

      {/* Overlay */}
      <Overlay $open={open} onClick={() => setOpen(false)} aria-hidden="true" />

      {/* Drawer */}
      <Drawer
        id="nav-drawer"
        ref={navRef}
        $open={open}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
      >
        <DrawerHeader>
          <Logo />
          <DrawerClose
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            ✕
          </DrawerClose>
        </DrawerHeader>

        <DrawerLinks role="menu">
          {NAV_LINKS.map(({ label, path }, i) => (
            <DrawerLinkItem key={path} $i={i}>
              <DrawerLink
                role="menuitem"
                $active={isActive(path)}
                aria-current={isActive(path) ? "page" : undefined}
                onClick={() => go(path)}
              >
                {label}
                <DrawerLinkArrow aria-hidden="true">›</DrawerLinkArrow>
              </DrawerLink>
            </DrawerLinkItem>
          ))}
        </DrawerLinks>

        <DrawerDivider aria-hidden="true" />

        <DrawerCta type="button" onClick={() => go("/contactUs")}>
          Contact Us →
        </DrawerCta>
      </Drawer>
    </>
  );
};

Navigation.displayName = "Navigation";
export default React.memo(Navigation);